import { useState, useEffect } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { trpc } from '@org/clients/school';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DatePicker,
  Skeleton,
} from '@org/ui';
import { parseDateOnly, toDateOnlyString } from '@org/utils-client';
import { useTeacherId } from '../../../../components/teacher-picker';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/students/$studentId/feedback/')({
  component: StudentFeedbackListPage,
});

const STATUS_CLASS_NAMES: Record<string, string> = {
  pending: 'bg-gray-100 text-gray-600',
  generating: 'bg-yellow-100 text-yellow-700 animate-pulse',
  generated: 'bg-blue-100 text-blue-700',
  published: 'bg-emerald-100 text-emerald-700',
};

function formatDate(date: Date | string | null): string {
  if (!date) return '-';
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('pt-BR');
}

function StudentFeedbackListPage() {
  const { t } = useTranslation('admin');
  const { studentId } = Route.useParams();
  const teacherId = useTeacherId();
  const navigate = useNavigate();

  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 3);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);

  const feedbackList = trpc.teacher.feedback.list.useQuery({ teacherId, studentId });
  const studentProfile = trpc.teacher.students.profile.useQuery({ teacherId, studentId });

  const studentName = studentProfile.data
    ? `${studentProfile.data.student.firstName ?? ''} ${studentProfile.data.student.lastName ?? ''}`.trim() || t('teacher.studentProfile.studentFallback')
    : t('teacher.studentProfile.studentFallback');

  // Smart default: last feedback's dateTo, or student's createdAt, or 3 months ago
  useEffect(() => {
    if (!feedbackList.data || !studentProfile.data) return;
    const lastFeedback = feedbackList.data[0]; // already sorted by createdAt desc
    if (lastFeedback?.dateTo) {
      const d = typeof lastFeedback.dateTo === 'string' ? lastFeedback.dateTo : new Date(lastFeedback.dateTo).toISOString().split('T')[0];
      setDateFrom(d);
    } else if (studentProfile.data.student.createdAt) {
      const d = new Date(studentProfile.data.student.createdAt).toISOString().split('T')[0];
      setDateFrom(d);
    }
  }, [feedbackList.data, studentProfile.data]);

  const generateMutation = trpc.teacher.feedback.generate.useMutation({
    onSuccess: (data) => {
      feedbackList.refetch();
      navigate({
        to: '/students/$studentId/feedback/$feedbackId',
        params: { studentId, feedbackId: data.feedbackId },
      });
    },
  });

  const handleGenerate = (type: 'midterm' | 'final') => {
    generateMutation.mutate({
      teacherId,
      studentId,
      dateFrom,
      dateTo,
      language: 'pt',
      type,
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="ghost" asChild className="px-0 text-sm text-[#cf2e2e]">
          <Link to="/students/$studentId" params={{ studentId }}>
            &#8592; {t('teacher.feedbackList.backToProfile')}
          </Link>
        </Button>
      </div>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-sm uppercase tracking-[0.3em] text-[#cf2e2e]">{t('teacher.feedbackList.eyebrow')}</p>
          <h1 className="text-3xl font-semibold text-gray-900">{studentName}</h1>
          <p className="text-sm text-gray-600">
            {t('teacher.feedbackList.description')}
          </p>
        </div>
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <label className="text-sm text-gray-600">{t('teacher.feedbackList.datePickerFrom')}</label>
            {/* Same shared DatePicker look as every other date field (no style overrides);
                values stay yyyy-MM-dd strings for the API. */}
            <DatePicker
              value={parseDateOnly(dateFrom)}
              onChange={(date) => setDateFrom(date ? toDateOnlyString(date) : '')}
              className="w-60"
            />
            <label className="text-sm text-gray-600">{t('teacher.feedbackList.datePickerTo')}</label>
            <DatePicker
              value={parseDateOnly(dateTo)}
              onChange={(date) => setDateTo(date ? toDateOnlyString(date) : '')}
              className="w-60"
            />
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => handleGenerate('midterm')}
              disabled={generateMutation.isPending}
              className="bg-[#cf2e2e] text-white hover:bg-[#b02525]"
            >
              {generateMutation.isPending ? t('teacher.feedbackList.generating') : t('teacher.feedbackList.newMidtermFeedback')}
            </Button>
            <Button
              onClick={() => handleGenerate('final')}
              disabled={generateMutation.isPending}
              variant="outline"
              className="border-[#cf2e2e] text-[#cf2e2e]"
            >
              {generateMutation.isPending ? t('teacher.feedbackList.generating') : t('teacher.feedbackList.newFinalFeedback')}
            </Button>
          </div>
        </div>
      </div>

      {feedbackList.isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i} className="border border-rose-50 bg-white">
              <CardHeader>
                <Skeleton className="h-5 w-1/3" />
                <Skeleton className="h-4 w-1/2" />
              </CardHeader>
            </Card>
          ))}
        </div>
      )}

      {!feedbackList.isLoading && (!feedbackList.data || feedbackList.data.length === 0) && (
        <Card className="border border-dashed border-rose-200 bg-white/80">
          <CardHeader>
            <CardTitle>{t('teacher.feedbackList.emptyTitle')}</CardTitle>
            <CardDescription>
              {t('teacher.feedbackList.emptyDescription')}
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {feedbackList.data && feedbackList.data.length > 0 && (
        <div className="space-y-3">
          {feedbackList.data.map((fb) => {
            const statusKey = fb.status as 'pending' | 'generating' | 'generated' | 'published';
            const badgeClassName = STATUS_CLASS_NAMES[statusKey] ?? STATUS_CLASS_NAMES.pending;
            return (
              <Link
                key={fb.feedbackId}
                to="/students/$studentId/feedback/$feedbackId"
                params={{ studentId, feedbackId: fb.feedbackId }}
              >
                <Card className="border border-rose-50 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Badge className={`text-xs ${badgeClassName}`}>
                          {t(`teacher.feedbackDashboard.statusLabels.${statusKey}`, t('teacher.feedbackDashboard.statusLabels.pending'))}
                        </Badge>
                        <Badge className="bg-gray-100 text-xs text-gray-500">
                          {fb.type === 'midterm' ? t('teacher.feedbackList.typeMidterm') : t('teacher.feedbackList.typeFinal')}
                        </Badge>
                        {fb.isOwn ? (
                          <Badge className="bg-rose-50 text-xs text-[#cf2e2e]">{t('teacher.feedbackList.yours')}</Badge>
                        ) : (
                          <Badge className="bg-sky-50 text-xs text-sky-800" title={t('teacher.feedbackList.readOnly')}>
                            {t('teacher.feedbackList.byTeacher', { name: fb.teacherName })}
                          </Badge>
                        )}
                      </div>
                      <span className="text-xs text-gray-500">
                        {t('teacher.feedbackList.createdAt', { date: formatDate(fb.createdAt) })}
                      </span>
                    </div>
                    <CardDescription className="mt-2">
                      {t('teacher.feedbackDetail.period', { from: formatDate(fb.dateFrom), to: formatDate(fb.dateTo) })}
                      {!fb.isOwn && <span className="ml-2 text-xs text-sky-800">· {t('teacher.feedbackList.readOnly')}</span>}
                    </CardDescription>
                  </CardHeader>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
